import { useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Tag,
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  Calendar,
  Percent,
  DollarSign,
  Gift,
  Package,
} from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { PageHeader } from '@/components/ui/page-header';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { EntityAutocomplete } from '@/components/ui/entity-autocomplete';

// ─── Types ───────────────────────────────────────────────────────────────────

type PromotionType = 'PERCENTAGE' | 'FIXED_AMOUNT' | 'BUY_X_GET_Y' | 'BUNDLE';
type ApplicableTo = 'ALL' | 'PRODUCTS' | 'CATEGORIES' | 'ORDER';

interface Promotion {
  id: string;
  name: string;
  description: string | null;
  type: PromotionType;
  discountValue: string;
  buyQuantity: number | null;
  getQuantity: number | null;
  minOrderValue: string | null;
  maxDiscount: string | null;
  startDate: string;
  endDate: string;
  isActive: boolean;
  applicableTo: ApplicableTo;
  usageLimit: number | null;
  usageCount: number;
  isTest: boolean;
  promotionProducts: { product: { id: string; name: string; sku: string } }[];
  promotionCategories: { category: { id: string; name: string } }[];
  code?: string | null;
  createdAt: string;
  // Happy hour (daysOfWeek is a JSON-encoded number[] string)
  recurringType?: '' | 'DAILY' | 'WEEKLY' | null;
  daysOfWeek?: string | null;
  timeOfDayStart?: string | null;
  timeOfDayEnd?: string | null;
}

interface PromotionDto {
  name: string;
  description?: string;
  type: PromotionType;
  discountValue: number;
  applicableTo: ApplicableTo;
  startDate: string;
  endDate: string;
  isActive: boolean;
  minOrderValue?: number;
  maxDiscount?: number;
  usageLimit?: number;
  code?: string;
  productIds?: string[];
  categoryIds?: string[];
  buyQuantity?: number;
  getQuantity?: number;
  recurringType?: 'DAILY' | 'WEEKLY';
  daysOfWeek?: number[];
  timeOfDayStart?: string;
  timeOfDayEnd?: string;
}

interface EntityRef {
  id: string;
  name: string;
}

interface PromotionFormData {
  name: string;
  description: string;
  type: PromotionType;
  discountValue: string;
  buyQuantity: string;
  getQuantity: string;
  minOrderValue: string;
  maxDiscount: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  applicableTo: ApplicableTo;
  usageLimit: string;
  code: string;
  products: EntityRef[];
  categories: EntityRef[];
  // Happy hour
  recurringType: '' | 'DAILY' | 'WEEKLY';
  daysOfWeek: number[];
  timeOfDayStart: string;
  timeOfDayEnd: string;
}

const emptyForm: PromotionFormData = {
  name: '',
  description: '',
  type: 'PERCENTAGE',
  discountValue: '',
  buyQuantity: '',
  getQuantity: '',
  minOrderValue: '',
  maxDiscount: '',
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  isActive: true,
  applicableTo: 'ALL',
  usageLimit: '',
  code: '',
  products: [],
  categories: [],
  recurringType: '',
  daysOfWeek: [],
  timeOfDayStart: '',
  timeOfDayEnd: '',
};

// ─── Type meta ────────────────────────────────────────────────────────────────

const typeConfig: Record<PromotionType, { label: string; icon: typeof Percent; color: string; desc: string }> = {
  PERCENTAGE: {
    label: 'Percentage',
    icon: Percent,
    color: 'text-info-text bg-info/10',
    desc: 'Discount by %',
  },
  FIXED_AMOUNT: {
    label: 'Fixed Amount',
    icon: DollarSign,
    color: 'text-success-text bg-success/10',
    desc: 'Fixed PKR off',
  },
  BUY_X_GET_Y: {
    label: 'Buy X Get Y',
    icon: Gift,
    color: 'text-primary-text bg-primary/10',
    desc: 'Free items',
  },
  BUNDLE: {
    label: 'Bundle',
    icon: Package,
    color: 'text-warning-text bg-warning/10',
    desc: 'Bundle deal',
  },
};

function RefChips({ items, onRemove }: { items: EntityRef[]; onRemove: (id: string) => void }) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {items.map((it) => (
        <span key={it.id} className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2.5 py-1 text-xs">
          {it.name}
          <button type="button" onClick={() => onRemove(it.id)} className="text-muted-foreground hover:text-destructive-text" aria-label={`Remove ${it.name}`}>
            <XCircle className="h-4 w-4" />
          </button>
        </span>
      ))}
    </div>
  );
}

// ─── Promotion Form ───────────────────────────────────────────────────────────

function PromotionForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
}: {
  initial: PromotionFormData;
  onSubmit: (data: PromotionFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [form, setForm] = useState<PromotionFormData>(initial);
  const { data: categoryOptions = [] } = useQuery({
    queryKey: ['categories', 'promotion-picker'],
    queryFn: () => api.get('/categories').then((r) => (r.data?.data ?? r.data ?? []) as EntityRef[]),
    enabled: form.applicableTo === 'CATEGORIES',
    staleTime: 60_000,
  });
  const set = <K extends keyof PromotionFormData>(key: K, value: PromotionFormData[K]) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Name */}
      <div className="space-y-1.5">
        <Label>Promotion Name *</Label>
        <Input
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="e.g. Eid Sale 20% Off"
          required
        />
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <Label>Description</Label>
        <Input
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Optional customer-facing description"
        />
      </div>

      {/* Type selector */}
      <div className="space-y-1.5">
        <Label>Promotion Type *</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {(Object.keys(typeConfig) as PromotionType[]).map((type) => {
            const cfg = typeConfig[type];
            const Icon = cfg.icon;
            return (
              <button
                key={type}
                type="button"
                onClick={() => set('type', type)}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-lg border p-3 text-xs font-medium transition-all',
                  form.type === type
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/40',
                )}
              >
                <Icon className={cn('h-5 w-5', cfg.color.split(' ')[0])} />
                <span>{cfg.label}</span>
                <span className="text-muted-foreground font-normal">{cfg.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Discount value */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>
            {form.type === 'PERCENTAGE' ? 'Discount %' : 'Discount Amount (PKR)'} *
          </Label>
          <Input
            type="number"
            min={form.type === 'PERCENTAGE' ? '0.01' : form.type === 'BUY_X_GET_Y' ? '0' : '1'}
            max={form.type === 'PERCENTAGE' ? '100' : undefined}
            step={form.type === 'PERCENTAGE' ? '0.01' : '1'}
            value={form.discountValue}
            onChange={(e) => set('discountValue', e.target.value)}
            placeholder={form.type === 'PERCENTAGE' ? '20' : '100'}
            required
          />
          {form.type === 'PERCENTAGE' && Number(form.discountValue) > 100 && (
            <p className="text-xs text-destructive">Discount % must be between 0.01 and 100</p>
          )}
        </div>

        {form.type === 'PERCENTAGE' && (
          <div className="space-y-1.5">
            <Label>Max Discount (PKR)</Label>
            <Input
              type="number"
              min="0"
              value={form.maxDiscount}
              onChange={(e) => set('maxDiscount', e.target.value)}
              placeholder="Optional cap"
            />
          </div>
        )}

        {form.type === 'BUY_X_GET_Y' && (
          <>
            <div className="space-y-1.5">
              <Label>Buy Quantity (X)</Label>
              <Input type="number" min="1" value={form.buyQuantity} onChange={(e) => set('buyQuantity', e.target.value)} placeholder="2" />
            </div>
            <div className="space-y-1.5">
              <Label>Get Quantity (Y free)</Label>
              <Input type="number" min="1" value={form.getQuantity} onChange={(e) => set('getQuantity', e.target.value)} placeholder="1" />
            </div>
          </>
        )}
      </div>

      {/* Applicable to */}
      <div className="space-y-1.5">
        <Label>Applies To</Label>
        <Dropdown
          value={form.applicableTo}
          onChange={(e) => set('applicableTo', e.value as ApplicableTo)}
          options={[
            { label: 'All Products', value: 'ALL' },
            { label: 'Order Total', value: 'ORDER' },
            { label: 'Specific Products', value: 'PRODUCTS' },
            { label: 'Specific Categories', value: 'CATEGORIES' },
          ]}
          optionLabel="label"
          optionValue="value"
          className="w-full"
        />
      </div>

      {form.applicableTo === 'PRODUCTS' && (
        <div className="space-y-1.5">
          <Label>Products *</Label>
          <EntityAutocomplete<{ id: string; name: string; sku?: string }>
            endpoint="/products"
            value={null}
            onChange={(p) => {
              if (p && !form.products.some((x) => x.id === p.id)) set('products', [...form.products, { id: p.id, name: p.name }]);
            }}
            getLabel={(p) => (p.sku ? `${p.name} (${p.sku})` : p.name)}
            placeholder="Search products to add…"
            dropdown={false}
          />
          <RefChips items={form.products} onRemove={(id) => set('products', form.products.filter((x) => x.id !== id))} />
        </div>
      )}
      {form.applicableTo === 'CATEGORIES' && (
        <div className="space-y-1.5">
          <Label>Categories *</Label>
          <Dropdown
            value={null}
            onChange={(e) => {
              const c = categoryOptions.find((x) => x.id === e.value);
              if (c && !form.categories.some((x) => x.id === c.id)) set('categories', [...form.categories, { id: c.id, name: c.name }]);
            }}
            options={categoryOptions.filter((c) => !form.categories.some((x) => x.id === c.id))}
            optionLabel="name"
            optionValue="id"
            placeholder="Add a category…"
            filter
            className="w-full"
          />
          <RefChips items={form.categories} onRemove={(id) => set('categories', form.categories.filter((x) => x.id !== id))} />
        </div>
      )}

      {/* Coupon code */}
      <div className="space-y-1.5">
        <Label>Coupon Code</Label>
        <Input
          value={form.code}
          onChange={(e) => set('code', e.target.value.toUpperCase())}
          placeholder="Optional — cashier enters this at checkout (e.g. EID2026)"
          maxLength={50}
        />
      </div>

      {/* Min order value */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Min Order Value (PKR)</Label>
          <Input
            type="number"
            min="0"
            value={form.minOrderValue}
            onChange={(e) => set('minOrderValue', e.target.value)}
            placeholder="Optional minimum"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Usage Limit</Label>
          <Input
            type="number"
            min="1"
            value={form.usageLimit}
            onChange={(e) => set('usageLimit', e.target.value)}
            placeholder="Unlimited"
          />
        </div>
      </div>

      {/* Date range */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Start Date *</Label>
          <Input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} required />
        </div>
        <div className="space-y-1.5">
          <Label>End Date *</Label>
          <Input type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} required />
        </div>
      </div>

      {/* Happy Hour / Time-Based Pricing */}
      <div className="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-4">
        <div className="flex items-center justify-between">
          <Label className="font-semibold">⏰ Happy Hour / Time-Based Pricing</Label>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Recurrence</Label>
          <Dropdown
            value={form.recurringType}
            onChange={(e) => set('recurringType', e.value as PromotionFormData['recurringType'])}
            options={[
              { label: 'None (date range only)', value: '' },
              { label: 'Daily (every day within date range)', value: 'DAILY' },
              { label: 'Weekly (specific days of week)', value: 'WEEKLY' },
            ]}
            optionLabel="label"
            optionValue="value"
            placeholder="None (date range only)"
            className="w-full"
          />
        </div>
        {form.recurringType === 'WEEKLY' && (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Days of Week</Label>
            <div className="flex flex-wrap gap-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => {
                    const current = form.daysOfWeek;
                    const next = current.includes(i) ? current.filter((d) => d !== i) : [...current, i];
                    set('daysOfWeek', next);
                  }}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-medium border transition-colors',
                    form.daysOfWeek.includes(i)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border hover:bg-accent',
                  )}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        )}
        {form.recurringType && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Start Time (HH:mm)</Label>
              <Input
                type="time"
                value={form.timeOfDayStart}
                onChange={(e) => set('timeOfDayStart', e.target.value)}
                placeholder="e.g. 16:00"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">End Time (HH:mm)</Label>
              <Input
                type="time"
                value={form.timeOfDayEnd}
                onChange={(e) => set('timeOfDayEnd', e.target.value)}
                placeholder="e.g. 18:00"
              />
            </div>
          </div>
        )}
      </div>

      {/* Active toggle */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => set('isActive', !form.isActive)}
          className={cn(
            'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
            form.isActive ? 'bg-success-solid' : 'bg-muted',
          )}
        >
          <span className={cn('inline-block h-3.5 w-3.5 transform rounded-full bg-background shadow transition-transform', form.isActive ? 'translate-x-4' : 'translate-x-0.5')} />
        </button>
        <span className="text-sm font-medium">{form.isActive ? 'Active' : 'Inactive'}</span>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2 border-t">
        <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button type="submit" className="flex-1" disabled={isLoading}>
          {isLoading ? 'Saving…' : 'Save Promotion'}
        </Button>
      </div>
    </form>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PromotionsPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('promotions:write') || hasPermission('promotions:*');

  const { t } = useTranslation('common');
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['promotions', page, activeOnly],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20', activeOnly: String(activeOnly) });
      return api.get(`/promotions?${params}`).then((r) => r.data);
    },
  });

  const promotions: Promotion[] = data?.data ?? [];
  const total: number = data?.total ?? 0;

  const createMutation = useMutation({
    mutationFn: (dto: object) => api.post('/promotions', dto).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: object }) =>
      api.patch(`/promotions/${id}`, dto).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      setEditingPromotion(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/promotions/${id}`).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['promotions'] }),
  });

  const handleFormSubmit = (formData: PromotionFormData) => {
    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      toast.error('End date must not be before the start date');
      return;
    }
    const value = parseFloat(formData.discountValue);
    if (formData.type === 'PERCENTAGE' && !(value > 0 && value <= 100)) {
      toast.error('Discount % must be between 0.01 and 100');
      return;
    }
    if ((formData.type === 'FIXED_AMOUNT' || formData.type === 'BUNDLE') && !(value > 0)) {
      toast.error('Discount amount must be greater than 0');
      return;
    }
    if (formData.type === 'BUY_X_GET_Y' && !(parseInt(formData.buyQuantity) >= 1 && parseInt(formData.getQuantity) >= 1)) {
      toast.error('Buy and Get quantities must each be at least 1');
      return;
    }
    if (formData.applicableTo === 'PRODUCTS' && formData.products.length === 0) {
      toast.error('Select at least one product');
      return;
    }
    if (formData.applicableTo === 'CATEGORIES' && formData.categories.length === 0) {
      toast.error('Select at least one category');
      return;
    }
    const dto: PromotionDto = {
      name: formData.name,
      description: formData.description || undefined,
      type: formData.type,
      discountValue: parseFloat(formData.discountValue),
      applicableTo: formData.applicableTo,
      startDate: formData.startDate,
      endDate: formData.endDate,
      isActive: formData.isActive,
    };
    if (formData.code.trim() || editingPromotion) dto.code = formData.code.trim();
    dto.productIds = formData.applicableTo === 'PRODUCTS' ? formData.products.map((p) => p.id) : [];
    dto.categoryIds = formData.applicableTo === 'CATEGORIES' ? formData.categories.map((c) => c.id) : [];
    if (formData.minOrderValue) dto.minOrderValue = parseFloat(formData.minOrderValue);
    if (formData.maxDiscount) dto.maxDiscount = parseFloat(formData.maxDiscount);
    if (formData.usageLimit) dto.usageLimit = parseInt(formData.usageLimit);
    if (formData.buyQuantity) dto.buyQuantity = parseInt(formData.buyQuantity);
    if (formData.getQuantity) dto.getQuantity = parseInt(formData.getQuantity);
    // Happy hour fields
    if (formData.recurringType) {
      dto.recurringType = formData.recurringType;
      if (formData.recurringType === 'WEEKLY' && formData.daysOfWeek.length > 0) dto.daysOfWeek = formData.daysOfWeek;
      if (formData.timeOfDayStart) dto.timeOfDayStart = formData.timeOfDayStart;
      if (formData.timeOfDayEnd) dto.timeOfDayEnd = formData.timeOfDayEnd;
    }

    if (editingPromotion) {
      updateMutation.mutate({ id: editingPromotion.id, dto });
    } else {
      createMutation.mutate(dto);
    }
  };

  const handleEdit = (promo: Promotion) => {
    setEditingPromotion(promo);
    setShowForm(false);
  };

  const isNowActive = (promo: Promotion) => {
    if (!promo.isActive) return false;
    const now = new Date();
    return new Date(promo.startDate) <= now && new Date(promo.endDate) >= now;
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Promotions"
        subtitle="Manage discount promotions — automatically applied at checkout"
        actions={canWrite && !showForm && !editingPromotion ? (
          <Button onClick={() => { setShowForm(true); setEditingPromotion(null); }} className="gap-2">
            <Plus className="h-4 w-4" />
            New Promotion
          </Button>
        ) : undefined}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {(Object.entries(typeConfig) as [PromotionType, typeof typeConfig[PromotionType]][]).map(([type, cfg]) => {
          const count = promotions.filter((p) => p.type === type).length;
          const Icon = cfg.icon;
          return (
            <div key={type} className="rounded-xl border bg-card p-4">
              <div className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium mb-2', cfg.color)}>
                <Icon className="h-3 w-3" />
                {cfg.label}
              </div>
              <p className="text-2xl font-bold tabular-nums">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Create/Edit form panel */}
      {(showForm || editingPromotion) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {editingPromotion ? 'Edit Promotion' : 'New Promotion'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PromotionForm
              initial={editingPromotion ? {
                name: editingPromotion.name,
                description: editingPromotion.description ?? '',
                type: editingPromotion.type,
                discountValue: editingPromotion.discountValue,
                buyQuantity: editingPromotion.buyQuantity?.toString() ?? '',
                getQuantity: editingPromotion.getQuantity?.toString() ?? '',
                minOrderValue: editingPromotion.minOrderValue ?? '',
                maxDiscount: editingPromotion.maxDiscount ?? '',
                startDate: editingPromotion.startDate.split('T')[0],
                endDate: editingPromotion.endDate.split('T')[0],
                isActive: editingPromotion.isActive,
                applicableTo: editingPromotion.applicableTo,
                usageLimit: editingPromotion.usageLimit?.toString() ?? '',
                code: editingPromotion.code ?? '',
                products: editingPromotion.promotionProducts.map((pp) => ({ id: pp.product.id, name: pp.product.name })),
                categories: editingPromotion.promotionCategories.map((pc) => ({ id: pc.category.id, name: pc.category.name })),
                recurringType: editingPromotion.recurringType ?? '',
                daysOfWeek: (() => { try { return JSON.parse(editingPromotion.daysOfWeek ?? '[]'); } catch { return []; } })(),
                timeOfDayStart: editingPromotion.timeOfDayStart ?? '',
                timeOfDayEnd: editingPromotion.timeOfDayEnd ?? '',
              } : emptyForm}
              onSubmit={handleFormSubmit}
              onCancel={() => { setShowForm(false); setEditingPromotion(null); }}
              isLoading={createMutation.isPending || updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {/* Promotions list */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="h-4 w-4 text-muted-foreground" />
              Promotions
              <span className="text-sm font-normal text-muted-foreground">({total})</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setActiveOnly(!activeOnly); setPage(1); }}
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all border',
                  activeOnly
                    ? 'bg-success/10 border-success/30 text-success-text'
                    : 'border-border text-muted-foreground hover:border-primary/40',
                )}
              >
                <CheckCircle className="h-3 w-3" />
                Active only
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="divide-y">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex gap-4 p-4 items-center">
                  <div className="h-10 w-10 rounded-lg bg-muted animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-48 rounded bg-muted animate-pulse" />
                    <div className="h-3 w-32 rounded bg-muted/60 animate-pulse" />
                  </div>
                  <div className="h-6 w-20 rounded-full bg-muted animate-pulse" />
                </div>
              ))}
            </div>
          ) : promotions.length === 0 ? (
            <EmptyState
              icon={Tag}
              title="No promotions yet"
              action={canWrite ? (
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowForm(true)}>
                  <Plus className="h-3.5 w-3.5" /> Create first promotion
                </Button>
              ) : undefined}
            />
          ) : (
            <div className="divide-y">
              {promotions.map((promo) => {
                const cfg = typeConfig[promo.type];
                const Icon = cfg.icon;
                const active = isNowActive(promo);
                const expired = new Date(promo.endDate) < new Date();

                return (
                  <div key={promo.id} className="flex items-start gap-4 p-4 hover:bg-muted/30 transition-colors">
                    <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', cfg.color)}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold">{promo.name}</p>
                        <StatusBadge
                          status={active ? 'ACTIVE' : expired ? 'EXPIRED' : 'INACTIVE'}
                          tone={active ? 'success' : expired ? 'danger' : 'secondary'}
                          label={active ? t('statusActive') : expired ? t('statusExpired') : t('statusInactive')}
                          icon={active ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                        />
                        {promo.isTest && (
                          <StatusBadge status="TEST" tone="info" label={t('statusTest')} />
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                        <span className={cn('font-medium', cfg.color.split(' ')[0])}>
                          {promo.type === 'PERCENTAGE' && `${promo.discountValue}% off`}
                          {promo.type === 'FIXED_AMOUNT' && `PKR ${promo.discountValue} off`}
                          {promo.type === 'BUY_X_GET_Y' && `Buy ${promo.buyQuantity} get ${promo.getQuantity} free`}
                          {promo.type === 'BUNDLE' && `Bundle deal`}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(promo.startDate).toLocaleDateString()} – {new Date(promo.endDate).toLocaleDateString()}
                        </span>
                        {promo.code && <span className="font-mono font-medium text-foreground">{promo.code}</span>}
                        {promo.usageLimit && (
                          <span>{promo.usageCount} / {promo.usageLimit} used</span>
                        )}
                        <span className="capitalize">{promo.applicableTo.toLowerCase().replace('_', ' ')}</span>
                      </div>
                      {promo.description && (
                        <p className="mt-1 text-xs text-muted-foreground">{promo.description}</p>
                      )}
                    </div>
                    {canWrite && (
                      <TableRowActions
                        actions={[
                          {
                            key: 'edit',
                            label: 'Edit',
                            icon: Pencil,
                            onSelect: () => handleEdit(promo),
                            primary: true,
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            icon: Trash2,
                            onSelect: () => {
                              if (confirm(`Delete "${promo.name}"?`)) deleteMutation.mutate(promo.id);
                            },
                            variant: 'destructive',
                          },
                        ]}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 20 && (
        <PaginationBar page={page} pageSize={20} total={total} onPageChange={setPage} />
      )}
    </div>
  );
}
